import { useState } from 'react';
import { Message as MessageType } from '@/types/chat.types';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

interface MessageProps {
  message: MessageType;
  onEmailRequest?: (message: string) => void;
}

export const Message = ({ message, onEmailRequest }: MessageProps) => {
  const isUser = message.role === 'user';
  const [feedback, setFeedback] = useState<'up' | 'down' | null>(null);
  const [showFeedbackForm, setShowFeedbackForm] = useState(false);
  const [feedbackText, setFeedbackText] = useState('');
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    await navigator.clipboard.writeText(message.content);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleThumbsUp = async () => {
    // Validate that message has a database ID (only assistant messages from DB)
    if (typeof message.id !== 'number') {
      console.warn('Cannot submit feedback for message without database ID:', message.id);
      return;
    }

    // Toggle thumbs up - if already up, delete it
    if (feedback === 'up') {
      try {
        await fetch(`/api/chat/feedback/${message.id}`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${sessionStorage.getItem('accessToken')}`
          }
        });
        setFeedback(null);
      } catch (error) {
        console.error('Failed to delete positive feedback:', error);
      }
      return;
    }

    setFeedback('up');
    setShowFeedbackForm(false);

    try {
      const response = await fetch('/api/chat/feedback', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${sessionStorage.getItem('accessToken')}`
        },
        body: JSON.stringify({
          messageId: message.id,
          feedback: '',
          rating: 'positive'
        })
      });

      if (!response.ok) {
        const error = await response.json();
        console.error('Failed to submit positive feedback:', error);
        setFeedback(null); // Reset on error
      }
    } catch (error) {
      console.error('Failed to submit positive feedback:', error);
      setFeedback(null); // Reset on error
    }
  };

  const handleThumbsDown = async () => {
    // Validate that message has a database ID (only assistant messages from DB)
    if (typeof message.id !== 'number') {
      console.warn('Cannot submit feedback for message without database ID:', message.id);
      return;
    }

    // Toggle thumbs down - if already down, delete it
    if (feedback === 'down') {
      try {
        await fetch(`/api/chat/feedback/${message.id}`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${sessionStorage.getItem('accessToken')}`
          }
        });
        setFeedback(null);
        setShowFeedbackForm(false);
      } catch (error) {
        console.error('Failed to delete negative feedback:', error);
      }
      return;
    }

    setFeedback('down');
    setShowFeedbackForm(true);
  };

  const handleSubmitFeedback = async () => {
    if (!feedbackText.trim()) return;

    // Validate that message has a database ID
    if (typeof message.id !== 'number') {
      console.warn('Cannot submit feedback for message without database ID:', message.id);
      return;
    }

    try {
      const response = await fetch('/api/chat/feedback', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${sessionStorage.getItem('accessToken')}`
        },
        body: JSON.stringify({
          messageId: message.id,
          feedback: feedbackText,
          rating: 'negative'
        })
      });

      if (response.ok) {
        setFeedbackText('');
        setShowFeedbackForm(false);
      } else {
        const error = await response.json();
        console.error('Failed to submit feedback:', error);
      }
    } catch (error) {
      console.error('Failed to submit feedback:', error);
    }
  };

  const handleEmailRequest = () => {
    if (onEmailRequest) {
      onEmailRequest('Summarize all the information we gathered in this session into a well organized report and email it to me.');
    }
  };

  return (
    <div className={`flex w-full ${isUser ? 'justify-end' : 'justify-start'} mb-6`}>
      <div
        className={`${isUser ? 'max-w-[70%]' : 'w-full'} rounded-2xl px-5 py-4 ${
          isUser
            ? 'bg-brand-teal text-neutral-white shadow-sm'
            : 'bg-neutral-light-gray text-neutral-black dark:bg-neutral-dark-gray dark:text-neutral-white'
        }`}
      >
        {isUser ? (
          <div className="whitespace-pre-wrap break-words">{message.content}</div>
        ) : (
          <div
            className="prose prose-sm max-w-none dark:prose-invert"
            style={{
              fontFamily: 'monospace',
              fontSize: '0.7em'
            }}
          >
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              components={{
                // Paragraphs
                p: ({ node, ...props }) => (
                  <p
                    {...props}
                    style={{
                      margin: '0.5em 0'
                    }}
                  />
                ),
                // Unordered and ordered lists
                ul: ({ node, ...props }) => (
                  <ul
                    {...props}
                    style={{
                      margin: '0.5em 0',
                      paddingLeft: '1.5em'
                    }}
                  />
                ),
                ol: ({ node, ...props }) => (
                  <ol
                    {...props}
                    style={{
                      margin: '0.5em 0',
                      paddingLeft: '1.5em'
                    }}
                  />
                ),
                li: ({ node, ...props }) => (
                  <li
                    {...props}
                    style={{
                      margin: '0.25em 0'
                    }}
                  />
                ),
                // Headings
                h1: ({ node, ...props }) => (
                  <h1
                    {...props}
                    style={{
                      margin: '0.75em 0 0.5em 0',
                      fontWeight: 'bold'
                    }}
                  />
                ),
                h2: ({ node, ...props }) => (
                  <h2
                    {...props}
                    style={{
                      margin: '0.75em 0 0.5em 0',
                      fontWeight: 'bold'
                    }}
                  />
                ),
                h3: ({ node, ...props }) => (
                  <h3
                    {...props}
                    style={{
                      margin: '0.75em 0 0.5em 0',
                      fontWeight: 'bold'
                    }}
                  />
                ),
                // Blockquotes
                blockquote: ({ node, ...props }) => (
                  <blockquote
                    {...props}
                    style={{
                      borderLeft: '3px solid #74AA9C',
                      paddingLeft: '1em',
                      margin: '0.5em 0',
                      fontStyle: 'italic'
                    }}
                  />
                ),
                // Images
                img: ({ node, ...props }) => (
                  <img
                    {...props}
                    className="max-w-full h-auto block"
                    alt={props.alt || ''}
                  />
                ),
                // Tables with custom styling
                table: ({ node, ...props }) => (
                  <div className="max-w-full max-h-96 overflow-auto my-2 border border-neutral-medium-gray/30 dark:border-neutral-dark-gray rounded-lg bg-neutral-white dark:bg-neutral-black">
                    <table
                      {...props}
                      className="border-collapse w-full"
                      style={{
                        fontSize: '0.85em',
                        fontFamily: 'Inter, OpenAI Sans, Segoe UI, Helvetica, Arial, sans-serif'
                      }}
                    />
                  </div>
                ),
                // Table headers with sticky positioning
                th: ({ node, ...props }) => (
                  <th
                    {...props}
                    className="border border-neutral-medium-gray/30 dark:border-neutral-dark-gray px-3 py-2 text-left whitespace-nowrap bg-neutral-light-gray dark:bg-neutral-dark-gray font-semibold sticky top-0 z-10"
                  />
                ),
                // Table cells
                td: ({ node, ...props }) => (
                  <td
                    {...props}
                    className="border border-neutral-medium-gray/30 dark:border-neutral-dark-gray px-3 py-2 text-left whitespace-nowrap"
                  />
                ),
                // Table rows with alternating colors and hover
                tr: ({ node, ...props }) => {
                  const isHeaderRow = props.children && Array.isArray(props.children)
                    && props.children.some((child: any) => child?.type === 'th');

                  if (isHeaderRow) {
                    return <tr {...props} />;
                  }

                  return (
                    <tr
                      {...props}
                      className="even:bg-neutral-light-gray/50 dark:even:bg-neutral-black hover:bg-brand-teal/10 dark:hover:bg-brand-teal/20 transition-colors"
                    />
                  );
                },
                // Code blocks
                code: ({ node, ...props }) => {
                  const isInline = !props.className?.includes('language-');
                  if (isInline) {
                    return (
                      <code
                        {...props}
                        style={{
                          backgroundColor: 'rgba(0, 0, 0, 0.05)',
                          padding: '0.2em 0.4em',
                          borderRadius: '3px',
                          fontFamily: 'monospace',
                          fontSize: '0.7em'
                        }}
                      />
                    );
                  }
                  return (
                    <code
                      {...props}
                      style={{
                        display: 'block',
                        backgroundColor: 'rgba(0, 0, 0, 0.05)',
                        padding: '0.75em',
                        borderRadius: '4px',
                        overflow: 'auto',
                        margin: '0.5em 0'
                      }}
                    />
                  );
                },
                // Links
                a: ({ node, ...props }) => (
                  <a
                    {...props}
                    style={{
                      color: '#74AA9C',
                      textDecoration: 'underline'
                    }}
                    target="_blank"
                    rel="noopener noreferrer"
                  />
                ),
              }}
            >
              {message.content}
            </ReactMarkdown>
          </div>
        )}
        <div
          className={`text-xs mt-2 ${
            isUser ? 'text-neutral-white/80' : 'text-neutral-medium-gray dark:text-neutral-medium-gray'
          }`}
        >
          {message.timestamp.toLocaleTimeString([], {
            hour: '2-digit',
            minute: '2-digit',
          })}
        </div>

        {/* Action buttons for assistant messages */}
        {!isUser && (
          <>
            <div className="flex items-center gap-2 mt-3 pt-3 border-t border-neutral-medium-gray/20">
              {/* Copy button */}
              <button
                onClick={handleCopy}
                className="p-2 hover:bg-neutral-medium-gray/10 rounded transition-colors"
                title="Copy to clipboard"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  {copied ? (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  ) : (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  )}
                </svg>
              </button>

              {/* Thumbs up button */}
              {feedback !== 'down' && (
                <button
                  onClick={handleThumbsUp}
                  className={`p-2 hover:bg-neutral-medium-gray/10 rounded transition-colors ${
                    feedback === 'up' ? 'bg-brand-teal/20 text-brand-teal' : ''
                  }`}
                  title="Good response"
                >
                  <svg className="w-4 h-4" fill={feedback === 'up' ? 'currentColor' : 'none'} stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 10h4.764a2 2 0 011.789 2.894l-3.5 7A2 2 0 0115.263 21h-4.017c-.163 0-.326-.02-.485-.06L7 20m7-10V5a2 2 0 00-2-2h-.095c-.5 0-.905.405-.905.905 0 .714-.211 1.412-.608 2.006L7 11v9m7-10h-2M7 20H5a2 2 0 01-2-2v-6a2 2 0 012-2h2.5" />
                  </svg>
                </button>
              )}

              {/* Thumbs down button */}
              {feedback !== 'up' && (
                <button
                  onClick={handleThumbsDown}
                  className={`p-2 hover:bg-neutral-medium-gray/10 rounded transition-colors ${
                    feedback === 'down' ? 'bg-red-500/20 text-red-500' : ''
                  }`}
                  title="Bad response"
                >
                  <svg className="w-4 h-4" fill={feedback === 'down' ? 'currentColor' : 'none'} stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14H5.236a2 2 0 01-1.789-2.894l3.5-7A2 2 0 018.736 3h4.018a2 2 0 01.485.06l3.76.94m-7 10v5a2 2 0 002 2h.096c.5 0 .905-.405.905-.904 0-.715.211-1.413.608-2.008L17 13V4m-7 10h2m5-10h2a2 2 0 012 2v6a2 2 0 01-2 2h-2.5" />
                  </svg>
                </button>
              )}

              {/* Email button */}
              <button
                onClick={handleEmailRequest}
                className="p-2 hover:bg-neutral-medium-gray/10 rounded transition-colors"
                title="Request summary email"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              </button>
            </div>

            {/* Feedback form */}
            {showFeedbackForm && (
              <div className="mt-3 pt-3 border-t border-neutral-medium-gray/20">
                <textarea
                  value={feedbackText}
                  onChange={(e) => setFeedbackText(e.target.value)}
                  placeholder="Please tell us what was wrong with this response..."
                  className="w-full p-3 border border-neutral-medium-gray/30 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-brand-teal dark:bg-neutral-dark-gray dark:text-neutral-white"
                  rows={3}
                />
                <button
                  onClick={handleSubmitFeedback}
                  disabled={!feedbackText.trim()}
                  className="mt-2 px-4 py-2 bg-brand-teal text-white rounded-lg hover:bg-brand-teal/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  Send Feedback
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};
